import React from "react";
import {
  MdOutlineCalendarMonth,
  MdOutlineLocalShipping,
  MdOutlineSchedule,
} from "react-icons/md";

export const DELIVERY_CHOICES = [
  {
    field: "express_delivery",
    optionValue: "Express Delivery",
    title: "Express delivery",
    description: "Fastest fulfilment when customers need items urgently.",
    Icon: MdOutlineLocalShipping,
  },
  {
    field: "deliver_slot_by_today",
    optionValue: "Deliver Slot by Today",
    title: "Delivery slot by today",
    description: "Let shoppers pick a same-day time window at checkout.",
    Icon: MdOutlineSchedule,
  },
  {
    field: "delivery_for_selected_date_slot",
    optionValue: "Delivery for a Selected Date and Slot",
    title: "Choose date & slot",
    description: "Schedule delivery on a future date with a specific slot.",
    Icon: MdOutlineCalendarMonth,
  },
];

const defaultDeliveryState = {
  express_delivery: true,
  deliver_slot_by_today: true,
  delivery_for_selected_date_slot: true,
  default_delivery_option: "Express Delivery",
};

export const getDefaultDeliveryOptions = () => ({ ...defaultDeliveryState });

const normalizeBool = (v, fallback) => {
  if (v === undefined || v === null) return fallback;
  return Boolean(v);
};

export const deliveryOptionsFromSite = (site) => {
  if (!site || typeof site !== "object") return getDefaultDeliveryOptions();
  return {
    express_delivery: normalizeBool(site.express_delivery, true),
    deliver_slot_by_today: normalizeBool(site.deliver_slot_by_today, true),
    delivery_for_selected_date_slot: normalizeBool(
      site.delivery_for_selected_date_slot,
      true
    ),
    default_delivery_option:
      site.default_delivery_option || defaultDeliveryState.default_delivery_option,
  };
};

const ensureValidDefault = (next) => {
  const current = next.default_delivery_option;
  const currentRow = DELIVERY_CHOICES.find((c) => c.optionValue === current);
  if (currentRow && next[currentRow.field]) return next;

  const firstOn = DELIVERY_CHOICES.find((c) => next[c.field]);
  return {
    ...next,
    default_delivery_option: firstOn
      ? firstOn.optionValue
      : current || defaultDeliveryState.default_delivery_option,
  };
};

export const normalizeDeliveryForSubmit = (opts) =>
  ensureValidDefault({ ...opts });

/**
 * @param {{ express_delivery: boolean, deliver_slot_by_today: boolean, delivery_for_selected_date_slot: boolean, default_delivery_option: string }} options
 * @param {(next: typeof options) => void} onOptionsChange
 * @param {boolean} readOnly
 */
const SiteDeliveryOptions = ({ options, onOptionsChange, readOnly }) => {
  const enabledCount = DELIVERY_CHOICES.filter((c) => options[c.field]).length;

  const setToggle = (field, checked) => {
    if (readOnly) return;
    let next = { ...options, [field]: checked };
    if (!checked && enabledCount <= 1 && options[field]) {
      return;
    }
    next = ensureValidDefault(next);
    onOptionsChange(next);
  };

  const setDefault = (optionValue) => {
    if (readOnly) return;
    const row = DELIVERY_CHOICES.find((c) => c.optionValue === optionValue);
    if (!row || !options[row.field]) return;
    onOptionsChange({ ...options, default_delivery_option: optionValue });
  };

  return (
    <section
      className={`site-delivery-card ${readOnly ? "site-delivery-card--readonly" : ""}`}
      aria-label="Delivery options"
    >
      <header className="site-delivery-card__header">
        <div>
          <h2 className="site-delivery-card__title">Delivery options</h2>
          <p className="site-delivery-card__subtitle">
            Choose which fulfilment modes appear at checkout and which one is
            pre-selected for customers.
          </p>
        </div>
      </header>

      <div className="site-delivery-card__grid">
        {DELIVERY_CHOICES.map(
          ({ field, title, description, optionValue, Icon }) => {
            const on = options[field];
            return (
              <div
                key={field}
                className={`site-delivery-row ${on ? "site-delivery-row--on" : ""}`}
              >
                <div className="site-delivery-row__icon" aria-hidden>
                  <Icon size={22} />
                </div>
                <div className="site-delivery-row__body">
                  <div className="site-delivery-row__title">{title}</div>
                  <p className="site-delivery-row__desc">{description}</p>
                </div>
                <label className="site-delivery-switch">
                  <input
                    type="checkbox"
                    checked={on}
                    disabled={readOnly || (on && enabledCount <= 1)}
                    onChange={(e) => setToggle(field, e.target.checked)}
                  />
                  <span className="site-delivery-switch__slider" />
                </label>
              </div>
            );
          }
        )}
      </div>

      <div className="site-delivery-default">
        <h3 className="site-delivery-default__heading">Default at checkout</h3>
        <p className="site-delivery-default__hint">
          This option is selected first for new carts. Only modes you enable
          above can be the default.
        </p>
        <div className="site-delivery-default__choices" role="radiogroup">
          {DELIVERY_CHOICES.map(({ field, optionValue, title }) => {
            const enabled = options[field];
            const selected =
              options.default_delivery_option === optionValue && enabled;
            return (
              <button
                key={field}
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={readOnly || !enabled}
                className={`site-delivery-pill ${selected ? "site-delivery-pill--selected" : ""} ${!enabled ? "site-delivery-pill--disabled" : ""}`}
                onClick={() => setDefault(optionValue)}
              >
                <span className="site-delivery-pill__radio" />
                <span className="site-delivery-pill__label">{title}</span>
              </button>
            );
          })}
        </div>
      </div>

      {!readOnly && (
        <p className="site-delivery-card__footer-note">
          Save together with the rest of site settings using{" "}
          <strong>Save</strong> below.
        </p>
      )}
    </section>
  );
};

export default SiteDeliveryOptions;
